/* eslint-disable css-modules/no-unused-class */
import { FC, useState } from 'react';
import { Divider, Icon, Tooltip } from 'learning-panel-components';
import { coreService } from 'network-utils';
import { formatFullTime } from '@components/learningPath/utils';
import { translate } from 'translations';
import LearningPathDetails from '../learningPathDetails/learningPathDetails';
import styles from './learningPathOverview.module.scss';

const TYPE_ICON_MAP: Record<string, string> = {
    lesson: `${process.env.PUBLIC_URL}/images/lessonTopIcon.svg`,
    course: `${process.env.PUBLIC_URL}/images/courseTopIcon.svg`,
    challenge: `${process.env.PUBLIC_URL}/images/challengeTopIcon.svg`,
    systemAssessment: `${process.env.PUBLIC_URL}/images/assessmentTopIcon.svg`,
    node: `${process.env.PUBLIC_URL}/images/mixTop.svg`,
};

const TYPE_LABEL_KEY: Record<string, string> = {
    systemAssessment: 'AssessmentContent',
};

const LearningPathOverview: FC<LearningPathOverviewProps> = ({ learningPathOverview, onClose }) => {
    const [expandedNode, setExpandedNode] = useState<string | null>(null);

    const toggleNode = (nodeIndex: string) => {
        setExpandedNode(prev => (prev === nodeIndex ? null : nodeIndex));
    };

    return (
        <div className={styles['learning-path-overview-wrapper']}>
            <div className={styles['learning-path-overview-header']}>
                <div className={styles['learning-path-overview-title']}>{learningPathOverview.name}</div>

                <div
                    className={styles['close-icon']}
                    onClick={e => {
                        e.stopPropagation();
                        onClose();
                    }}
                >
                    <Icon name="exitIcon" />
                </div>
            </div>

            <Divider className={styles.divider} />
            <LearningPathDetails
                score={learningPathOverview.points}
                progress={learningPathOverview.progress}
                duration={formatFullTime(learningPathOverview.duration)}
                certificate
            />
            <Divider className={styles.divider} />
            <div className={styles['nodes-summary']}>
                <Icon name="pathThin" />
                <div className={styles['nodes-summary-text']}>
                    {translate('TotalItems_x', { count: String(learningPathOverview.nodes.reduce((sum, node) => sum + node.items.length, 0)) })}
                </div>
            </div>
            <div className={styles['nodes-accordion']}>
                {learningPathOverview.nodes.map((node, nodeIndex) => (
                    <div key={nodeIndex} className={styles['accordion-item']}>
                        <div
                            className={styles['accordion-header']}
                            onClick={() => toggleNode(String(nodeIndex))}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => e.key === 'Enter' && toggleNode(String(nodeIndex))}
                        >
                            <span className={styles['accordion-title']}>{node.name}</span>
                            <Icon name={expandedNode === String(nodeIndex) ? 'unExpand' : 'expand'} />
                        </div>
                        {expandedNode === String(nodeIndex) && (
                            <div className={styles['accordion-content']}>
                                {node.items.map((item: { name: string; status: string; type: string; url?: string }, index: number) => (
                                    <div key={index} className={styles['accordion-row']}>
                                        {item.status === 'completed' && <Icon name="blueCheck" />}
                                        {TYPE_ICON_MAP[item.type] && (
                                            <Tooltip placement="top" title={<div>{translate(TYPE_LABEL_KEY[item.type] ?? item.type)}</div>}>
                                                <img
                                                    src={TYPE_ICON_MAP[item.type]}
                                                    className={styles['accordion-type-icon']}
                                                    alt={item.type}
                                                />
                                            </Tooltip>
                                        )}
                                        <span className={styles['accordion-item-name']}>{item.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export interface LearningPathOverviewProps {
    learningPathOverview: coreService.LearningPathOverview;
    onClose: (...args: any[]) => any;
}

export default LearningPathOverview;
